import React, {cloneElement, Component, createRef, PropsWithChildren, RefObject} from "react";
import {observer} from "mobx-react";

import classNames from "classnames";
import styles from "./styles/Modal.module.scss";
import zIndex from "../../styles/_zIndex.module.scss";
import {CSSTransition} from "react-transition-group";
import Button from "../utilities/Button";
import FontAwesome from "../utilities/FontAwesome";
import {StoreContext} from "../../stores/StoreContext";
import {AppContextType} from "../../stores/StoreLoader";
import ModalBackdrop from "./_ModalBackdrop";
import {ErrorBoundary} from "../utilities/rollbar/ErrorBoundary";
import {withTranslation, WithTranslation} from 'react-i18next';
import {ThemeProvider} from "../radix/ThemeProvider";

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

@observer
class Modal extends Component<PropsWithChildren<{
    size: "sm" | "md" | "lg" | "xl" | "sb"
    theme?: "classic" | "transparent"
    index: number
    type: string
    className?: string
    dialogClassName?: string
    containerClassName?: string
    modalClassName?: string
    width?: number
    closeEffect?: () => void,
    transitionClassNames?: object
    closeButtonClassName?: string
    closeButtonComponent?: React.ReactElement<any>
    active: boolean
} & WithTranslation>, {
    previousFocus: HTMLElement | null
}> {
    static contextType = StoreContext;
    // @ts-ignore
    public context: AppContextType;

    public modalRef: RefObject<HTMLDivElement | null>;
    public baseZIndex: number;

    static defaultProps = {
        size: "lg",
        type: 'default',
        theme: "classic",
    };

    constructor(props) {
        super(props);
        this.state = {
            previousFocus: document.activeElement as HTMLElement | null,
        }
        this.modalRef = createRef();
        this.baseZIndex = Number(zIndex["z-index-modal"]);
    }

    onMouseDown = (event) => {
        const target = event.target;
        if (!(this.modalRef.current as HTMLDivElement).contains(target)) {
            event.stopPropagation();
            this.context.modalStore.removeTop();
        }
    };

    onKeyDown = (event) => {
        switch (event.key) {
            case 'Esc':
            case 'Escape':
                this.context.modalStore.removeTop();
                break;
        }
    }

    focusFirstElement = () => {
        this.modalRef.current?.focus();
    }

    componentDidMount() {
        if (this.context.interfaceStore.ExecutionEnvironment.canUseDOM) {
            document.body.classList.add(styles.modalOpen);
        }

        document.addEventListener("keydown", this.onKeyDown);
        this.focusFirstElement();
    }

    componentWillUnmount() {
        if (this.context.interfaceStore.ExecutionEnvironment.canUseDOM && this.context.modalStore.stack.length === 0) {
            document.body.classList.remove(styles.modalOpen);
        }

        document.removeEventListener("keydown", this.onKeyDown);

        this.props.closeEffect && this.props.closeEffect();
        this.state.previousFocus && this.state.previousFocus.focus();
    }

    render() {
        const {index, size = "lg", type = "default", theme = "classic"} = this.props;

        let backdropZIndex = this.baseZIndex + index * 10,
            modalZIndex = backdropZIndex + 1;

        const closeClassName = classNames({
            [styles.close]: true,
            [styles.closeTransparent]: theme === 'transparent',
            [styles.closeAdmin]: type === 'admin',
            [this.props.closeButtonClassName || ""]: this.props.closeButtonClassName,
        });
        const modalContentClassName = classNames({
            [styles.modalContent]: true,
            [styles.modalContentTransparent]: theme === 'transparent',
            [this.props.className || ""]: this.props.className,
        });
        const modalClassName = classNames({
            [styles.modal]: true,
            [styles.modalTransparent]: theme === 'transparent',
            [this.props.modalClassName || ""]: this.props.modalClassName,
        });
        const dialogClassName = classNames({
            [styles.modalDialog]: true,
            [styles.modalDialogTransparent]: theme === 'transparent',
            [this.props.dialogClassName || ""]: this.props.dialogClassName,
            [styles.modalSm]: size === "sm",
            [styles.modalMd]: size === "md",
            [styles.modalLg]: size === "lg",
            [styles.modalSchoolblocks]: size === "sb",
            [styles.modalXl]: size === "xl",
        });
        const modalContainerClassName = classNames({
            [styles.modalContainer]: true,
            [styles.modalContainerTransparent]: theme === 'transparent',
            [this.props.containerClassName || ""]: this.props.containerClassName,
        });

        const ButtonComponent = this.props.closeButtonComponent ? cloneElement(this.props.closeButtonComponent, {
            ...this.props.closeButtonComponent.props,
            "aria-label": this.props.t('Close'),
            onClick: this.context.modalStore.removeTop,
        }, this.props.closeButtonComponent.props.children) : <Button
            className={closeClassName}
            onClick={this.context.modalStore.removeTop}
            id="modalCloseButton"
            aria-label={this.props.t('Close')}
        >
            <FontAwesome ariaHidden={true} prefix={'fas'} name={'fa-times'}/>
        </Button>

        return (
            <div>
                <ModalBackdrop
                    active={this.props.active}
                    onMouseDown={this.onMouseDown}
                    opaqueBackground={true}
                    zIndex={backdropZIndex}
                />
                <div
                    className={modalClassName}
                    style={{zIndex: modalZIndex}}
                    onMouseDown={this.onMouseDown}
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                >
                    {theme === 'transparent' && <Button
                        className={closeClassName}
                        onClick={this.context.modalStore.removeTop}
                        id="modalCloseButton"
                        aria-label={this.props.t('Close')}
                    >
                        <FontAwesome ariaHidden={true} prefix={'fas'} name={'fa-circle-xmark'}/>
                    </Button>}
                    <div
                        className={dialogClassName}
                        style={this.props.width ? {maxWidth: this.props.width} : {}}
                        role="document"
                    >
                        <CSSTransition timeout={0} appear={true} in={this.props.active} nodeRef={this.modalRef}
                                       classNames={this.props.transitionClassNames || transitionClassNames}>
                            <ThemeProvider theme={{portalContainer: this.modalRef}}>
                                <div tabIndex={0}
                                     ref={this.modalRef}
                                     className={modalContainerClassName}>
                                    {theme !== 'transparent' && ButtonComponent}
                                    <div tabIndex={0}
                                         className={modalContentClassName}>
                                        <ErrorBoundary>
                                            {this.props.children}
                                        </ErrorBoundary>
                                    </div>
                                    <span tabIndex={0} onFocus={this.focusFirstElement}/>
                                </div>
                            </ThemeProvider>
                        </CSSTransition>
                    </div>
                </div>
            </div>
        );
    }
}

export default withTranslation('translation')(Modal)
